/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import uuid from 'uuid/v4';

import type { DocNode } from '@atlaskit/adf-schema';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import type { Fragment, Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { Transform } from '@atlaskit/editor-prosemirror/transform';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { ReactRenderer } from '@atlaskit/renderer';
import { RendererActionsContext } from '@atlaskit/renderer/actions';
import { token } from '@atlaskit/tokens';

import { getSlice } from '../prebuilt/config-items-range/utils';
import { getDiffObjectsBetweenParagraphs } from '../utils/diff-match-patch/utils';
import type { IgnoredRange, ParagraphChunk } from '../utils/diff-match-patch/utils';

const RED_SUBSTITUTE_HEX = '#AAAAA0';
const BLUE_SUBSTITUTE_HEX = '#AAAAA1';

// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
const styles = css`
	span[data-text-custom-color='${RED_SUBSTITUTE_HEX}'] {
		background-color: ${token('color.background.accent.red.subtlest', '#FFEBE6')};
		text-decoration: line-through;

		--custom-palette-color: inherit !important;
		user-select: none;
	}
	span[data-text-custom-color='${BLUE_SUBSTITUTE_HEX}'] {
		background-color: ${token('color.background.accent.blue.subtlest', '#DEEBFF')};
		text-decoration: underline;
		text-decoration-style: dashed;

		--custom-palette-color: inherit !important;
	}
`;

/**
 * Helper function to trim empty paragraphs as there isn't much value in
 * diffing empty paragraphs.
 */
function trimEmptyParagraphs(paragraphs: ParagraphChunk[]) {
	if (!paragraphs.length) {
		return paragraphs;
	}
	const trimmedParagraphs = [...paragraphs];

	while (trimmedParagraphs.length > 0 && trimmedParagraphs[0].text === '') {
		trimmedParagraphs.shift();
	}

	while (
		trimmedParagraphs.length > 0 &&
		trimmedParagraphs[trimmedParagraphs.length - 1].text === ''
	) {
		trimmedParagraphs.pop();
	}
	return trimmedParagraphs;
}

const jsonTransformer = new JSONTransformer();

const getIgnoredRanges = (node: PMNode) => {
	const ignoredRanges: Array<IgnoredRange> = [];
	node.descendants((node, pos) => {
		if (!node.isText && node.isInline) {
			ignoredRanges.push({ pos, size: node.nodeSize, type: 'inlineNode' });
		}
	});
	return ignoredRanges;
};

export function createDiffPreview({
	editorView,
	pmFragment,
	positions,
	providerFactory,
}: {
	editorView: EditorView;
	pmFragment: Fragment;
	positions: [start: number, end: number];
	providerFactory?: ProviderFactory;
}): React.ReactElement | undefined {
	const { schema } = editorView.state;

	// If schema doesn't have textColor, underline, or strike, we won't be able to render the highlights
	if (!schema.marks?.textColor) {
		return;
	}

	const slice = getSlice(editorView, positions);
	if (slice.openStart !== slice.openEnd) {
		return;
	}

	const paragraphType = schema.nodes.paragraph;
	let content = slice.content;

	/**
	 * Edge case for when the slice is a single line - the content will begin and end
	 * with inline nodes - to make it convertable to a document we wrap it in a paragraph
	 */
	if (content.firstChild?.isInline && content.lastChild?.isInline) {
		const wrappedContent = schema.nodes.doc.createAndFill(
			{},
			schema.nodes.paragraph.create({}, content),
		);
		if (wrappedContent) {
			content = wrappedContent as unknown as Fragment;
		}
	}

	if (!content) {
		return;
	}
	const inputParagraphs: ParagraphChunk[] = [];

	content.descendants((node, pos) => {
		if (node.type === paragraphType) {
			const ignoredRanges = getIgnoredRanges(node);
			inputParagraphs.push({
				id: uuid(),
				text: node.textContent,
				from: pos + 1,
				to: pos + node.nodeSize - 1,
				ignoredRanges,
			});
		}
	});

	const outputParagraphs: ParagraphChunk[] = [];

	pmFragment.descendants((node, pos) => {
		if (node.type === paragraphType) {
			const ignoredRanges = getIgnoredRanges(node);
			outputParagraphs.push({
				id: uuid(),
				text: node.textContent,
				from: pos + 1,
				to: pos + node.nodeSize - 1,
				ignoredRanges,
			});
		}
	});

	const trimmedInputParagraphs = trimEmptyParagraphs(inputParagraphs);
	const trimmedOutputParagraphs = trimEmptyParagraphs(outputParagraphs);

	if (trimmedInputParagraphs.length !== trimmedOutputParagraphs.length) {
		return;
	}

	const diffObjects = getDiffObjectsBetweenParagraphs(
		trimmedOutputParagraphs,
		trimmedInputParagraphs,
	);

	const docFrom = schema.nodes.doc.createAndFill({}, pmFragment);
	if (!docFrom) {
		return;
	}
	const transform = new Transform(docFrom);

	let offset = 0;
	diffObjects.forEach(({ from, to, text, type }) => {
		switch (type) {
			case 'ADDITION':
			case 'REPLACEMENT': {
				transform.addMark(
					offset + from,
					offset + to,
					schema.marks.textColor.create({
						color: BLUE_SUBSTITUTE_HEX,
					}),
				);
				return;
			}
			case 'DELETION': {
				transform.insert(offset + to, schema.text(text));
				transform.addMark(
					offset + to,
					offset + to + text.length,
					schema.marks.textColor.create({ color: RED_SUBSTITUTE_HEX }),
				);
				offset += text.length;
				return;
			}
			default:
				return;
		}
	});

	let document;
	try {
		document = jsonTransformer.encode(transform.doc) as DocNode;
	} catch (e) {}

	if (!document) {
		return;
	}

	return (
		<div data-testid="diff-renderer" css={styles}>
			<RendererActionsContext>
				<ReactRenderer
					document={document}
					adfStage="stage0"
					media={{
						// We don't currently support images with captions
						allowLinking: false,
					}}
					schema={schema}
					// By default -- the renderer uses a "default schema" which supports all
					// the nodes and marks the markdown transformer can create.
					// see packages/editor/adf-schema/src/schema/default-schema.ts for details.
					useSpecBasedValidator
					// this can only be safely removed once products have upgraded to a compatible version of renderer.
					dataProviders={providerFactory}
					allowPlaceholderText
				/>
			</RendererActionsContext>
		</div>
	);
}
